import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core';
import { Form, Formik, getIn, useFormikContext } from 'formik';
import * as yup from 'yup';
import i18n from 'i18n-js';
import html2canvas from 'html2canvas';
import agent from 'api/agent';
import { useAppSelector } from 'index';
import { images } from 'assets';
import { DayKey } from 'components/restaurantInformation/components/types';
import { FormikTimePicker } from 'components/shared';
import { parseTimeToMinutes } from 'helpers/orderInAdvanceHelper';
import {
    getDefaultReservationRequestTimes,
    getReservationRequestTimesIssues,
    hasWorkingHours,
    RESERVATION_REQUEST_DAY_KEYS,
} from 'helpers/reservationRequestTimesHelper';
import restaurantStore from 'store/restaurantDetailsStore';
import {
    ReservationModuleSettings,
    RestaurantDetails,
    ReservationRequestTimes,
} from 'store/restaurantDetailsStore/reducer';
import globalStore from 'store/globalStore';
import { useThunkDispatch } from '../hooks';
import ReservationRequestTimesSection from './ReservationRequestTimesSection';
import ReservationBlockedDatesSection from './ReservationBlockedDatesSection';
import { getRestaurantWallClockString } from 'helpers/reservationHelper';
import { BlockedDateIssue, formatBlockedDateIssue, validateBlockedDates } from 'helpers/reservationBlockedDatesHelper';
import { ReservationBlockedDate } from 'store/restaurantDetailsStore/reducer';

interface ReservationSettingsFormValues {
    onlineRequestBlockedDates: ReservationBlockedDate[];
    isOnlineAvailable: boolean;
    isSameDayAvailable: boolean;
    bookingHorizon: number | '';
    maxGuestsOnline: number | '';
    maxReservationDurationMinutes: number;
    sameDayUntilTime: number | null;
    sameDayUntilTimeInputInvalid: boolean;
    conditionsText: string;
    requestTimesFollowDineInWorkingHours: boolean;
    requestTimes: ReservationRequestTimes | null;
}

interface Props {
    canEdit: boolean;
    onDirtyChange: (dirty: boolean) => void;
    onSavingChange: (saving: boolean) => void;
    submitFormRef: React.MutableRefObject<() => void>;
    resetFormRef: React.MutableRefObject<() => void>;
}

function isBlockedDatesValidationError(error: unknown): error is {
    Errors: Record<string, BlockedDateIssue[]>;
} {
    return typeof error === 'object' && error !== null && 'Errors' in error;
}

const dayKeys: DayKey[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
];

function getOpeningIntervals(restaurant: RestaurantDetails): Array<[number, number]> {
    const workingHours = restaurant?.dineIn?.workingHours;
    if (!workingHours) return [];

    return dayKeys.reduce<Array<[number, number]>>((intervals, day) => {
        (workingHours[day] || []).forEach(
            (hours: { from: number | string; to: number | string }) => {
                const from = parseTimeToMinutes(hours.from);
                const to = parseTimeToMinutes(hours.to);
                if (from === null || to === null) return;
                if (from === 0 && to === 0) {
                    intervals.push([0, 24 * 60]);
                } else if (from <= to) {
                    intervals.push([from, to]);
                } else {
                    intervals.push([from, 24 * 60], [0, to]);
                }
            }
        );
        return intervals;
    }, []);
}

function getInitialValues(module: ReservationModuleSettings): ReservationSettingsFormValues {
    const hasSavedSettings =
        (module.bookingHorizon ?? 0) > 0 ||
        (module.maxGuestsOnline ?? 0) > 0 ||
        module.maxReservationDurationMinutes > 0;

    return {
        onlineRequestBlockedDates: module.onlineRequestBlockedDates ?? [],
        isOnlineAvailable: module.isOnlineAvailable,
        isSameDayAvailable: hasSavedSettings ? module.isSameDayAvailable : false,
        bookingHorizon:
            module.bookingHorizon !== null && module.bookingHorizon > 0
                ? module.bookingHorizon
                : 60,
        maxGuestsOnline:
            module.maxGuestsOnline !== null && module.maxGuestsOnline > 0
                ? module.maxGuestsOnline
                : 8,
        maxReservationDurationMinutes: module.maxReservationDurationMinutes || 120,
        sameDayUntilTime:
            module.sameDayUntilTime > 0
                ? minutesToPickerValue(parseTimeToMinutes(module.sameDayUntilTime) ?? 0)
                : null,
        sameDayUntilTimeInputInvalid: false,
        conditionsText: module.conditionsText ?? '',
        requestTimesFollowDineInWorkingHours:
            module.requestTimesFollowDineInWorkingHours !== false,
        requestTimes: module.requestTimes ?? null,
    };
}

function getRequestTimesForSubmit(
    followsWorkingHours: boolean,
    requestTimes: ReservationRequestTimes | null
): ReservationRequestTimes | null {
    if (followsWorkingHours || !requestTimes) return null;
    return RESERVATION_REQUEST_DAY_KEYS.reduce<ReservationRequestTimes>((result, day) => {
        const settings = requestTimes[day];
        if (settings) {
            result[day] = {
                ...settings,
                periods: settings.periods.filter(
                    (period) => period.from != null && period.to != null
                ),
            };
        }
        return result;
    }, {});
}

function minutesToPickerValue(minutes: number): number {
    return Math.round((Math.floor(minutes / 60) + (minutes % 60) / 100) * 100) / 100;
}

function getDurationLabel(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    if (minutes % 60 === 0) return `${minutes / 60} h`;
    return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

function FocusFirstError() {
    const { errors, submitCount } = useFormikContext<ReservationSettingsFormValues>();
    const fieldOrder: (keyof ReservationSettingsFormValues)[] = [
        'bookingHorizon',
        'maxGuestsOnline',
        'maxReservationDurationMinutes',
        'sameDayUntilTime',
        'requestTimes',
        'onlineRequestBlockedDates',
        'conditionsText',
    ];

    useEffect(() => {
        if (submitCount === 0) return;
        const firstError = fieldOrder.find((field) => errors[field]);
        if (firstError) document.getElementById(firstError)?.focus();
    }, [submitCount, errors]);

    return null;
}

function DirtyStateTracker({
    canEdit,
    onDirtyChange,
}: {
    canEdit: boolean;
    onDirtyChange: (dirty: boolean) => void;
}) {
    const { dirty } = useFormikContext<ReservationSettingsFormValues>();

    useEffect(() => {
        onDirtyChange(canEdit && dirty);
    }, [canEdit, dirty, onDirtyChange]);

    return null;
}

function ExpiredBlockedDatesTracker({ timeZone }: { timeZone: string | null | undefined }) {
    const form = useFormikContext<ReservationSettingsFormValues>();
    const [today, setToday] = useState(() => getRestaurantWallClockString(timeZone).slice(0, 10));

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        const refresh = () => {
            clearTimeout(timer);
            const now = getRestaurantWallClockString(timeZone);
            setToday(now.slice(0, 10));
            const [hours, minutes, seconds] = now.slice(11).split(':').map(Number);
            const untilMidnight = (86400 - hours * 3600 - minutes * 60 - seconds) * 1000;
            timer = setTimeout(refresh, Math.min(untilMidnight, 60000));
        };
        refresh();
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', refresh);
        };
    }, [timeZone]);

    useEffect(() => {
        if (form.isSubmitting) return;
        const expired = form.initialValues.onlineRequestBlockedDates.filter(
            (entry) => (entry.toDate ?? entry.fromDate) < today
        );
        if (!expired.length) return;
        const keepDraftEntry = (entry: ReservationBlockedDate) => !expired.some(
            (saved) => saved.id === entry.id && saved.fromDate === entry.fromDate &&
                saved.toDate === entry.toDate && saved.reason === entry.reason
        );
        const keepIndices = form.values.onlineRequestBlockedDates
            .map((entry, index) => keepDraftEntry(entry) ? index : -1)
            .filter((index) => index >= 0);
        const values = {
            ...form.values,
            onlineRequestBlockedDates: keepIndices.map((index) => form.values.onlineRequestBlockedDates[index]),
        };
        const errors = { ...form.errors };
        const entryErrors = keepIndices.map((index) =>
            getIn(form.errors, `onlineRequestBlockedDates.${index}`) ?? {}
        );
        if (entryErrors.some((error) => Object.keys(error).length > 0)) {
            errors.onlineRequestBlockedDates = entryErrors;
        } else {
            delete errors.onlineRequestBlockedDates;
        }
        const touched = {
            ...form.touched,
            onlineRequestBlockedDates: keepIndices.map((index) =>
                getIn(form.touched, `onlineRequestBlockedDates.${index}`) ?? {}
            ),
        };
        // Move the baseline as well, so automatic expiry neither dirties the form nor returns on CANCEL.
        form.resetForm({
            values: {
                ...form.initialValues,
                onlineRequestBlockedDates: form.initialValues.onlineRequestBlockedDates.filter(
                    (entry) => !expired.some((saved) => saved.id === entry.id)
                ),
            },
            submitCount: form.submitCount,
        });
        form.setValues(values, false);
        form.setErrors(errors);
        form.setTouched(touched, false);
    }, [today, form]);

    return null;
}

export default function ReservationSettings({
    canEdit,
    onDirtyChange,
    onSavingChange,
    submitFormRef,
    resetFormRef,
}: Props) {
    const dispatch = useThunkDispatch();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const [module, setModule] = useState<ReservationModuleSettings | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const openingIntervals = useMemo(() => getOpeningIntervals(restaurant), [restaurant]);
    const qrDownloadRef = useRef<HTMLDivElement>(null);
    const requestUrl = `${(
        process.env.REACT_APP_WEBAPP_URL || 'https://dev.gastrojames.ch'
    ).replace(/\/+$/, '')}/restaurant/${restaurant?.id}/reservation`;
    const QRCode = require('qrcode.react');

    useEffect(() => {
        let active = true;
        setModule(null);
        setLoadFailed(false);
        if (canEdit && restaurant?.id) {
            agent.Restaurants.GetReservationModule()
                .then((settings) => {
                    if (active) setModule(settings);
                })
                .catch((error) => {
                    if (active) {
                        setLoadFailed(true);
                        dispatch(globalStore.actionCreators.showToaster('error', error));
                    }
                });
        }
        return () => {
            active = false;
        };
    }, [canEdit, restaurant?.id, loadAttempt, dispatch]);

    const validationSchema = useMemo(
        () =>
            yup.object().shape({
                conditionsText: yup
                    .string()
                    .max(500, i18n.t('restaurant.reservationSettings.conditionsTextMaxLength')),
                bookingHorizon: yup
                    .number()
                    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
                    .typeError(i18n.t('restaurant.reservationSettings.bookingHorizonRangeError'))
                    .integer(i18n.t('restaurant.reservationSettings.bookingHorizonRangeError'))
                    .required(i18n.t('restaurant.reservationSettings.required'))
                    .min(1, i18n.t('restaurant.reservationSettings.bookingHorizonRangeError'))
                    .max(365, i18n.t('restaurant.reservationSettings.bookingHorizonRangeError')),
                maxGuestsOnline: yup
                    .number()
                    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
                    .typeError(i18n.t('restaurant.reservationSettings.maxGuestsRangeError'))
                    .integer(i18n.t('restaurant.reservationSettings.maxGuestsRangeError'))
                    .required(i18n.t('restaurant.reservationSettings.required'))
                    .min(1, i18n.t('restaurant.reservationSettings.maxGuestsRangeError'))
                    .max(20, i18n.t('restaurant.reservationSettings.maxGuestsRangeError')),
                maxReservationDurationMinutes: yup
                    .number()
                    .required(i18n.t('restaurant.reservationSettings.required'))
                    .min(30, i18n.t('restaurant.reservationSettings.durationRangeError'))
                    .max(360, i18n.t('restaurant.reservationSettings.durationRangeError'))
                    .test(
                        '30-minute-increments',
                        i18n.t('restaurant.reservationSettings.durationRangeError'),
                        (value) => value !== undefined && value % 30 === 0
                    ),
                sameDayUntilTime: yup
                    .number()
                    .nullable()
                    .test(
                        'valid-time',
                        i18n.t('restaurant.reservationSettings.invalidTime'),
                        function (value) {
                            if (!this.parent.isOnlineAvailable || !this.parent.isSameDayAvailable) {
                                return true;
                            }
                            if (this.parent.sameDayUntilTimeInputInvalid) {
                                return false;
                            }
                            if (value === null || value === undefined) {
                                return true;
                            }
                            const minutes = parseTimeToMinutes(value);
                            return (
                                minutes !== null &&
                                Number.isInteger(minutes) &&
                                minutes >= 0 &&
                                minutes < 24 * 60
                            );
                        }
                    )
                    .test(
                        'within-opening-hours',
                        i18n.t('restaurant.reservationSettings.timeOutsideHours'),
                        function (value) {
                            if (
                                !this.parent.isOnlineAvailable ||
                                !this.parent.isSameDayAvailable ||
                                value === null ||
                                value === undefined
                            ) {
                                return true;
                            }
                            const minutes = parseTimeToMinutes(value);
                            if (minutes === null) return true;
                            return openingIntervals.some(
                                ([from, to]) => minutes >= from && minutes <= to
                            );
                        }
                    ),
            }),
        [openingIntervals, i18n.locale]
    );

    async function copyRequestLink() {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(requestUrl);
            } else {
                const temporaryInput = document.createElement('textarea');
                temporaryInput.value = requestUrl;
                temporaryInput.style.position = 'fixed';
                temporaryInput.style.opacity = '0';
                document.body.appendChild(temporaryInput);
                temporaryInput.select();
                const copied = document.execCommand('copy');
                temporaryInput.remove();
                if (!copied) throw new Error('Clipboard copy is not supported');
            }
            globalStore.actionCreators.showToaster(
                'success',
                i18n.t('restaurant.reservationSettings.linkCopied')
            )(dispatch);
        } catch (error) {
            globalStore.actionCreators.showToaster('error', error)(dispatch);
        }
    }

    async function downloadQrCode() {
        const container = qrDownloadRef.current;
        if (!container) {
            globalStore.actionCreators.showToaster(
                'error',
                i18n.t('restaurant.reservationSettings.qrDownloadFailed')
            )(dispatch);
            return;
        }

        try {
            const canvas = await html2canvas(container, {
                scale: 2,
                onclone: (_document, element) => {
                    element.style.display = 'flex';
                },
            });
            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${(restaurant.name || 'restaurant').replace(
                /[<>:"/\\|?*]/g,
                '-'
            )}-reservation-qr.png`;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            globalStore.actionCreators.showToaster(
                'error',
                i18n.t('restaurant.reservationSettings.qrDownloadFailed')
            )(dispatch);
        }
    }

    if (!canEdit) return null;

    if (loadFailed) {
        return (
            <Box p={2}>
                <Button onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
                    {i18n.t('restaurant.reservationSettings.retry')}
                </Button>
            </Box>
        );
    }

    if (!restaurant?.id || !module) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Formik<ReservationSettingsFormValues>
            initialValues={getInitialValues(module)}
            validationSchema={validationSchema}
            validate={(values) => {
                const blockedErrors = validateBlockedDates(
                    values.onlineRequestBlockedDates, module.onlineRequestBlockedDates ?? [],
                    getRestaurantWallClockString(restaurant.timeZone).slice(0, 10)
                );
                const issues = values.requestTimesFollowDineInWorkingHours ? {} : getReservationRequestTimesIssues(
                    values.requestTimes,
                    restaurant.dineIn?.workingHours,
                    values.maxReservationDurationMinutes
                );
                const dayErrors = RESERVATION_REQUEST_DAY_KEYS.reduce<
                    Partial<Record<DayKey, string>>
                >((result, day) => {
                    if (issues[day]?.length) {
                        result[day] = issues[day]!
                            .map((issue) =>
                                i18n.t(
                                    `restaurant.reservationSettings.${issue.key}`,
                                    issue.params || {}
                                )
                            )
                            .join('\n');
                    }
                    return result;
                }, {});
                return {
                    ...(Object.keys(dayErrors).length > 0 ? { requestTimes: dayErrors } : {}),
                    ...(blockedErrors.some((error) => error.reason)
                        ? { onlineRequestBlockedDates: blockedErrors } : {}),
                };
            }}
            onSubmit={async (values, actions) => {
                if (!canEdit) return;

                onSavingChange(true);
                try {
                    const savedModule = await dispatch(
                        restaurantStore.actionCreators.updateReservationModule({
                            onlineRequestBlockedDates: values.onlineRequestBlockedDates,
                            isOnlineAvailable: values.isOnlineAvailable,
                            isSameDayAvailable: values.isSameDayAvailable,
                            bookingHorizon: Number(values.bookingHorizon),
                            maxGuestsOnline: Number(values.maxGuestsOnline),
                            maxReservationDurationMinutes: values.maxReservationDurationMinutes,
                            conditionsText: values.conditionsText.trim(),
                            sameDayUntilTime:
                                values.sameDayUntilTime === null
                                    ? 0
                                    : minutesToPickerValue(
                                          parseTimeToMinutes(values.sameDayUntilTime) ?? 0
                                      ),
                            requestTimesFollowDineInWorkingHours:
                                values.requestTimesFollowDineInWorkingHours,
                            requestTimes: getRequestTimesForSubmit(
                                values.requestTimesFollowDineInWorkingHours,
                                values.requestTimes
                            ),
                        })
                    );
                    setModule(savedModule);
                    actions.resetForm({
                        values: getInitialValues(savedModule),
                    });
                } catch (error) {
                    if (isBlockedDatesValidationError(error)) {
                        values.onlineRequestBlockedDates.forEach((entry, index) => {
                            const issues = error.Errors[entry.id];
                            if (issues?.length) actions.setFieldError(
                                `onlineRequestBlockedDates.${index}.reason`,
                                issues.map(formatBlockedDateIssue).join('\n')
                            );
                        });
                    }
                } finally {
                    onSavingChange(false);
                }
            }}
        >
            {({
                values,
                errors,
                touched,
                handleBlur,
                handleChange,
                setFieldValue,
                submitForm,
                resetForm,
                isSubmitting,
                submitCount,
            }) => {
                submitFormRef.current = submitForm;
                resetFormRef.current = () => resetForm();

                return (
                    <Form>
                        <FocusFirstError />
                        <ExpiredBlockedDatesTracker timeZone={restaurant.timeZone} />
                        <DirtyStateTracker canEdit={canEdit} onDirtyChange={onDirtyChange} />

                        <Box px={2} pb={3}>
                            <Typography variant="caption" display="block" gutterBottom>
                                {i18n
                                    .t('restaurant.reservationSettings.onlineRequestsTitle')
                                    .toUpperCase()}
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={values.isOnlineAvailable}
                                        onChange={(_, checked) =>
                                            setFieldValue('isOnlineAvailable', checked)
                                        }
                                        color="primary"
                                        disabled={!canEdit || isSubmitting}
                                    />
                                }
                                label={i18n.t(
                                    'restaurant.reservationSettings.onlineRequestsToggle'
                                )}
                            />

                            <Box
                                display="flex"
                                alignItems="center"
                                mt={1}
                                style={{
                                    opacity: values.isOnlineAvailable ? 1 : 0.4,
                                    pointerEvents: values.isOnlineAvailable ? 'auto' : 'none',
                                }}
                            >
                                <Box
                                    border={1}
                                    borderColor="grey.300"
                                    borderRadius={4}
                                    p={1}
                                    mr={1}
                                    bgcolor="white"
                                >
                                    <div>
                                        <QRCode value={requestUrl} size={78} />
                                    </div>
                                </Box>
                                <Box display="flex" gridGap={12}>
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        color="primary"
                                        onClick={copyRequestLink}
                                        disabled={!values.isOnlineAvailable}
                                    >
                                        {i18n
                                            .t('restaurant.reservationSettings.copyLink')
                                            .toUpperCase()}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        color="primary"
                                        onClick={downloadQrCode}
                                        disabled={!values.isOnlineAvailable}
                                    >
                                        {i18n
                                            .t('restaurant.reservationSettings.downloadQr')
                                            .toUpperCase()}
                                    </Button>
                                </Box>
                            </Box>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                                style={{ maxWidth: 560, marginTop: 10 }}
                            >
                                {i18n.t('restaurant.reservationSettings.shareDescription')}
                            </Typography>

                            <div
                                ref={qrDownloadRef}
                                aria-hidden="true"
                                style={{
                                    display: 'none',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 340,
                                    padding: '15px 2px',
                                    background:
                                        'linear-gradient(180deg, #ffd79a 9.9%, #ff9f9f 100%)',
                                }}
                            >
                                <span
                                    style={{
                                        width: 275,
                                        margin: '-5px 0 10px',
                                        overflowWrap: 'break-word',
                                        textAlign: 'center',
                                        fontWeight: 700,
                                    }}
                                >
                                    {restaurant.name}
                                </span>
                                <QRCode
                                    value={requestUrl}
                                    size={300}
                                    includeMargin
                                    level="Q"
                                    imageSettings={{
                                        src: images.icons.blackPenguin,
                                        width: 75,
                                        height: 75,
                                    }}
                                    style={{ borderRadius: 8 }}
                                />
                                <span
                                    style={{
                                        width: 275,
                                        margin: '6px 0 -5px',
                                        overflowWrap: 'break-word',
                                        textAlign: 'center',
                                        fontWeight: 700,
                                    }}
                                >
                                    {i18n.t('restaurant.reservationSettings.qrCaption')}
                                </span>
                            </div>

                            <Box mt={3}>
                                <Typography variant="caption" display="block" gutterBottom>
                                    {i18n
                                        .t('restaurant.reservationSettings.limitsTitle')
                                        .toUpperCase()}
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gridGap={14}>
                                    <TextField
                                        id="bookingHorizon"
                                        name="bookingHorizon"
                                        type="number"
                                        variant="filled"
                                        label={i18n.t(
                                            'restaurant.reservationSettings.bookingHorizon'
                                        )}
                                        value={values.bookingHorizon}
                                        onChange={(event) =>
                                            setFieldValue(
                                                'bookingHorizon',
                                                event.target.value === ''
                                                    ? ''
                                                    : Number(event.target.value)
                                            )
                                        }
                                        onBlur={handleBlur}
                                        error={Boolean(
                                            touched.bookingHorizon && errors.bookingHorizon
                                        )}
                                        helperText={
                                            touched.bookingHorizon && errors.bookingHorizon
                                                ? errors.bookingHorizon
                                                : i18n.t(
                                                      'restaurant.reservationSettings.bookingHorizonHelper'
                                                  )
                                        }
                                        FormHelperTextProps={{
                                            style: {
                                                display:
                                                    touched.bookingHorizon && errors.bookingHorizon
                                                        ? 'block'
                                                        : 'none',
                                            },
                                        }}
                                        inputProps={{ min: 1, max: 365, step: 1 }}
                                        disabled={
                                            !canEdit || !values.isOnlineAvailable || isSubmitting
                                        }
                                        style={{ width: 280, maxWidth: '100%' }}
                                    />
                                    <TextField
                                        id="maxGuestsOnline"
                                        name="maxGuestsOnline"
                                        type="number"
                                        variant="filled"
                                        label={i18n.t('restaurant.reservationSettings.maxGuests')}
                                        value={values.maxGuestsOnline}
                                        onChange={(event) =>
                                            setFieldValue(
                                                'maxGuestsOnline',
                                                event.target.value === ''
                                                    ? ''
                                                    : Number(event.target.value)
                                            )
                                        }
                                        onBlur={handleBlur}
                                        error={Boolean(
                                            touched.maxGuestsOnline && errors.maxGuestsOnline
                                        )}
                                        helperText={
                                            touched.maxGuestsOnline && errors.maxGuestsOnline
                                                ? errors.maxGuestsOnline
                                                : i18n.t(
                                                      'restaurant.reservationSettings.maxGuestsHelper'
                                                  )
                                        }
                                        FormHelperTextProps={{
                                            style: {
                                                display:
                                                    touched.maxGuestsOnline &&
                                                    errors.maxGuestsOnline
                                                        ? 'block'
                                                        : 'none',
                                            },
                                        }}
                                        inputProps={{ min: 1, max: 20, step: 1 }}
                                        disabled={
                                            !canEdit || !values.isOnlineAvailable || isSubmitting
                                        }
                                        style={{ width: 250, maxWidth: '100%' }}
                                    />
                                    <TextField
                                        variant="filled"
                                        style={{ width: 800, maxWidth: '100%' }}
                                        InputProps={{
                                            style: { width: 280, maxWidth: '100%' },
                                        }}
                                        disabled={!canEdit || isSubmitting}
                                        id="maxReservationDurationMinutes"
                                        select
                                        name="maxReservationDurationMinutes"
                                        label={i18n.t(
                                            'restaurant.reservationSettings.maxReservationDuration'
                                        )}
                                        value={values.maxReservationDurationMinutes}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(
                                            touched.maxReservationDurationMinutes &&
                                                errors.maxReservationDurationMinutes
                                        )}
                                        helperText={
                                            touched.maxReservationDurationMinutes &&
                                            errors.maxReservationDurationMinutes
                                                ? errors.maxReservationDurationMinutes
                                                : i18n.t(
                                                      'restaurant.reservationSettings.durationHelper'
                                                  )
                                        }
                                        FormHelperTextProps={{
                                            style: {
                                                display:
                                                    touched.maxReservationDurationMinutes &&
                                                    errors.maxReservationDurationMinutes
                                                        ? 'block'
                                                        : 'none',
                                            },
                                        }}
                                    >
                                        {Array.from({ length: 12 }, (_, index) => {
                                            const minutes = (index + 1) * 30;
                                            return (
                                                <MenuItem key={minutes} value={minutes}>
                                                    {getDurationLabel(minutes)}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                </Box>
                            </Box>

                            <Box mt={2}>
                                <Typography variant="caption" display="block" gutterBottom>
                                    {i18n
                                        .t('restaurant.reservationSettings.sameDayTitle')
                                        .toUpperCase()}
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values.isSameDayAvailable}
                                            onChange={(_, checked) =>
                                                setFieldValue('isSameDayAvailable', checked)
                                            }
                                            color="primary"
                                            disabled={
                                                !canEdit ||
                                                !values.isOnlineAvailable ||
                                                isSubmitting
                                            }
                                        />
                                    }
                                    label={i18n.t('restaurant.reservationSettings.sameDayToggle')}
                                />
                                <Box mt={1} style={{ width: 307, maxWidth: '100%' }}>
                                    <FormikTimePicker
                                        id="sameDayUntilTime"
                                        name="sameDayUntilTime"
                                        label={i18n.t(
                                            'restaurant.reservationSettings.sameDayCutoff'
                                        )}
                                        invalidDateMessage={i18n.t(
                                            'restaurant.reservationSettings.invalidTime'
                                        )}
                                        helperText={i18n.t(
                                            'restaurant.reservationSettings.sameDayCutoffHelper'
                                        )}
                                        showErrorBelow
                                        onInvalidInputChange={(isInvalid) =>
                                            setFieldValue('sameDayUntilTimeInputInvalid', isInvalid)
                                        }
                                        disabled={
                                            !canEdit ||
                                            !values.isOnlineAvailable ||
                                            !values.isSameDayAvailable ||
                                            isSubmitting
                                        }
                                        style={{ width: '100%' }}
                                    />
                                </Box>
                            </Box>

                            {(() => {
                                const savedIssues =
                                    !module.requestTimesFollowDineInWorkingHours &&
                                    module.requestTimes
                                        ? getReservationRequestTimesIssues(
                                              module.requestTimes,
                                              restaurant.dineIn?.workingHours,
                                              module.maxReservationDurationMinutes || 120
                                          )
                                        : {};
                                if (module.requestTimes) {
                                    RESERVATION_REQUEST_DAY_KEYS.forEach((day) => {
                                        const settings = module.requestTimes?.[day];
                                        if (
                                            settings?.isEnabled &&
                                            settings.periods.length > 0 &&
                                            !hasWorkingHours(
                                                restaurant.dineIn?.workingHours,
                                                day
                                            )
                                        ) {
                                            savedIssues[day] = [
                                                { key: 'requestTimesClosedNow' },
                                            ];
                                        }
                                    });
                                }
                                const currentIssues =
                                    values.requestTimesFollowDineInWorkingHours
                                        ? {}
                                        : getReservationRequestTimesIssues(
                                              values.requestTimes,
                                              restaurant.dineIn?.workingHours,
                                              values.maxReservationDurationMinutes
                                          );
                                const validationErrors = RESERVATION_REQUEST_DAY_KEYS.reduce<
                                    Partial<Record<DayKey, string>>
                                >((result, day) => {
                                    if (currentIssues[day]?.length) {
                                        result[day] = currentIssues[day]!
                                            .map((issue) =>
                                                i18n.t(
                                                    `restaurant.reservationSettings.${issue.key}`,
                                                    issue.params || {}
                                                )
                                            )
                                            .join('\n');
                                    }
                                    return result;
                                }, {});
                                const warningErrors = RESERVATION_REQUEST_DAY_KEYS.reduce<
                                    Partial<Record<DayKey, string>>
                                >((result, day) => {
                                    if (savedIssues[day]?.length) {
                                        result[day] = savedIssues[day]!
                                            .map((issue) =>
                                                i18n.t(
                                                    `restaurant.reservationSettings.${issue.key}`,
                                                    issue.params || {}
                                                )
                                            )
                                            .join('\n');
                                    }
                                    return result;
                                }, {});
                                const warningDays = RESERVATION_REQUEST_DAY_KEYS.reduce<
                                    Partial<Record<DayKey, boolean>>
                                >((result, day) => {
                                    if (savedIssues[day]?.length) result[day] = true;
                                    return result;
                                }, {});
                                return (
                                    <ReservationRequestTimesSection
                                        workingHours={restaurant.dineIn?.workingHours}
                                        canEdit={canEdit}
                                        isSubmitting={isSubmitting}
                                        submitCount={submitCount}
                                        validationErrors={validationErrors}
                                        warningErrors={warningErrors}
                                        warningDays={warningDays}
                                        savedRequestTimes={module.requestTimes}
                                        savedDurationMinutes={
                                            module.maxReservationDurationMinutes || 120
                                        }
                                        onModeChange={(follows) => {
                                            if (!follows && !values.requestTimes) {
                                                setFieldValue(
                                                    'requestTimes',
                                                    getDefaultReservationRequestTimes(
                                                        restaurant.dineIn?.workingHours,
                                                        values.maxReservationDurationMinutes
                                                    )
                                                );
                                            }
                                            setFieldValue(
                                                'requestTimesFollowDineInWorkingHours',
                                                follows
                                            );
                                        }}
                                    />
                                );
                            })()}

                            <ReservationBlockedDatesSection />
                            <Box mt={3}>
                                <Typography variant="caption" display="block" gutterBottom>
                                    {i18n
                                        .t('restaurant.reservationSettings.conditionsTextTitle')
                                        .toUpperCase()}
                                </Typography>
                                <TextField
                                    id="conditionsText"
                                    name="conditionsText"
                                    variant="filled"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label={i18n.t('restaurant.reservationSettings.conditionsText')}
                                    value={values.conditionsText}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={Boolean(errors.conditionsText)}
                                    helperText={
                                        errors.conditionsText ||
                                        i18n.t(
                                            'restaurant.reservationSettings.conditionsTextHelper'
                                        )
                                    }
                                    disabled={!canEdit || isSubmitting}
                                />
                            </Box>

                            {!values.isOnlineAvailable && (
                                <Box
                                    mt={2}
                                    p={2}
                                    bgcolor="grey.100"
                                    borderRadius={4}
                                    maxWidth={620}
                                >
                                    <Typography variant="body2">
                                        {i18n.t('restaurant.reservationSettings.onlineRequestsOff')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
}
